import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlarmClock,
  CalendarCheck2,
  CalendarClock,
  CalendarPlus,
  CalendarX2,
  Check,
  Clock,
  Hourglass,
  LifeBuoy,
  ListTodo,
  Mail,
  PhoneCall,
  Reply,
  UserPlus,
  type LucideIcon,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@gokrew/ui/components/dropdown-menu';
import { useAuth } from '@/context/AuthContext';
import {
  completeCrmActivity,
  getCrmAttention,
  getCrmInboxes,
  getCrmThreadLink,
  getCrmMembers,
  handleCrmSupport,
  snoozeCrmAttention,
} from '@/api/crm';
import type { CrmAttentionItem, CrmAttentionKind } from '@/api/crm';
import { personInitials, personName, useCrmFormat } from './crm-format';
import {
  attentionContext,
  attentionMessage,
  inAttentionScope,
  readAttentionScope,
  replyLink,
  saveAttentionScope,
  waitedFor,
  CRM_MAIL_APPS,
  isDesktopMailApp,
  mailAppLink,
  readMailApp,
  replySource,
  saveMailApp,
  type CrmAttentionScope,
  type CrmMailApp,
} from './crm-attention';
import { CrmButton, CrmError, CrmSectionTitle } from './CrmShared';
import styles from './Crm.module.css';

const ICON: Record<CrmAttentionKind, LucideIcon> = {
  support_request: LifeBuoy,
  email_unanswered: Mail,
  task_overdue: AlarmClock,
  close_passed: CalendarX2,
  meeting_followup: PhoneCall,
  stalled: Hourglass,
  close_soon: CalendarClock,
  no_next_step: CalendarPlus,
  task_due: ListTodo,
  leads_waiting: UserPlus,
};

/** Rows shown before "Show all". */
const FIRST_ROWS = 8;

/**
 * The dashboard's Needs your attention list (JP, 2026-10-31): what an
 * assistant would point out each morning, from crm-attention.rules.ts.
 */
export const CrmAttention = ({ onDeal }: { onDeal: (id: string) => void }) => {
  const { t, date, i18n } = useCrmFormat();
  const { user } = useAuth();
  const navigate = useNavigate();
  const client = useQueryClient();
  const [scope, setScope] = useState<CrmAttentionScope>(readAttentionScope);
  const [expanded, setExpanded] = useState(false);
  const [tab, setTab] = useState<'sales' | 'support'>('sales');
  const [mailApp, setMailApp] = useState<CrmMailApp | null>(readMailApp);
  const inboxes = useQuery({
    queryKey: ['crm', 'inboxes'],
    queryFn: getCrmInboxes,
  });
  const [busy, setBusy] = useState<string | null>(null);
  const attention = useQuery({
    queryKey: ['crm', 'attention'],
    queryFn: getCrmAttention,
  });
  const members = useQuery({
    queryKey: ['crm', 'members'],
    queryFn: getCrmMembers,
    enabled: scope === 'team',
  });
  const chooseScope = (next: CrmAttentionScope) => {
    setScope(next);
    saveAttentionScope(next);
  };
  const removeLine = (key: string) =>
    client.setQueryData<CrmAttentionItem[]>(['crm', 'attention'], (old) =>
      old?.filter((line) => line.key !== key)
    );
  const snooze = async (item: CrmAttentionItem, days: 1 | 7) => {
    setBusy(item.key);
    try {
      await snoozeCrmAttention(item.key, days);
      removeLine(item.key);
    } finally {
      setBusy(null);
    }
  };
  const complete = async (item: CrmAttentionItem) => {
    if (!item.dealId || !item.activityId) return;
    setBusy(item.key);
    try {
      await completeCrmActivity(item.dealId, item.activityId);
      removeLine(item.key);
      await client.invalidateQueries({ queryKey: ['crm'] });
    } finally {
      setBusy(null);
    }
  };
  /**
   * Opens the email's own conversation in the member's mail app, so the
   * reply keeps the thread. Mail that reached a colleague's mailbox, or a
   * lookup that fails, falls back to a new email to the sender.
   */
  const reply = async (item: CrmAttentionItem, app: CrmMailApp) => {
    if (app !== mailApp) {
      saveMailApp(app);
      setMailApp(app);
    }
    const fallback = replyLink(item);
    const source = replySource(item);
    // A browser tab must open within the click, before the lookup.
    const tab = isDesktopMailApp(app) ? null : window.open('', '_blank');
    let url: string | null = null;
    if (source && item.activityId)
      try {
        const link = await getCrmThreadLink(source, item.activityId);
        const mine = (inboxes.data ?? []).some(
          (inbox) =>
            inbox.status === 'connected' &&
            inbox.email?.toLowerCase() === link.mailboxEmail
        );
        if (mine) url = mailAppLink(app, link);
      } catch {
        url = null;
      }
    if (url && tab) {
      tab.location.href = url;
      return;
    }
    tab?.close();
    const target = url ?? fallback;
    if (target) window.location.href = target;
  };
  const handle = async (item: CrmAttentionItem) => {
    if (!item.activityId) return;
    setBusy(item.key);
    try {
      await handleCrmSupport(item.activityId);
      removeLine(item.key);
    } finally {
      setBusy(null);
    }
  };
  const open = (item: CrmAttentionItem) =>
    item.dealId ? onDeal(item.dealId) : navigate('/crm/leads');
  const sentence = (item: CrmAttentionItem) => {
    const { key, params } = attentionMessage(item);
    return t(`crm.attention.${key}`, {
      ...params,
      stage: item.stage ? t(`crm.workspace.stages.${item.stage}`) : '',
      date: date(item.at),
      when: item.at ? waitedFor(item.at, i18n.language) : '',
    });
  };
  const scoped = (attention.data ?? []).filter((item) =>
    inAttentionScope(item, scope, user?.id)
  );
  // Prospects' to-dos and real customers' questions never share a list.
  const lists = {
    sales: scoped.filter((item) => item.category !== 'support'),
    support: scoped.filter((item) => item.category === 'support'),
  };
  const items = lists[tab];
  const chooseTab = (next: 'sales' | 'support') => {
    setTab(next);
    setExpanded(false);
  };
  const urgent = items.filter((item) => item.severity === 'danger').length;
  const shown = expanded ? items : items.slice(0, FIRST_ROWS);
  return (
    <section className={`${styles.panel} ${styles.attentionPanel}`}>
      <div className={styles.attentionHeading}>
        <CrmSectionTitle title={t('crm.attention.title')} />
        <div
          className={styles.valueSwitch}
          role="group"
          aria-label={t('crm.attention.scope')}
        >
          {(['mine', 'team'] as const).map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={scope === option}
              onClick={() => chooseScope(option)}
            >
              {t(`crm.attention.scope_${option}`)}
            </button>
          ))}
        </div>
      </div>
      <div className={styles.feedTabs}>
        {(['sales', 'support'] as const).map((key) => (
          <button
            key={key}
            type="button"
            className={tab === key ? styles.feedTabActive : ''}
            onClick={() => chooseTab(key)}
          >
            {key === 'support' && <LifeBuoy />}
            {t(`crm.attention.tab_${key}`)}
            {attention.isSuccess && (
              <span className={styles.tabCount}>{lists[key].length}</span>
            )}
          </button>
        ))}
      </div>
      {attention.isPending ? (
        <p role="status" className={styles.loading}>
          {t('crm.loading')}
        </p>
      ) : attention.isError ? (
        <CrmError retry={() => void attention.refetch()} />
      ) : items.length ? (
        <>
          <p className={styles.panelCaption}>
            {urgent
              ? t('crm.attention.summary_urgent', {
                  count: items.length,
                  urgent,
                })
              : t('crm.attention.summary', { count: items.length })}
          </p>
          <ul className={styles.attentionList}>
            {shown.map((item) => {
              const Icon = ICON[item.kind];
              const replyTo = replyLink(item);
              const owner =
                scope === 'team' && item.ownerUserId
                  ? members.data?.find((m) => m.userId === item.ownerUserId)
                  : undefined;
              const isTask =
                item.kind === 'task_overdue' || item.kind === 'task_due';
              const isSupport = item.kind === 'support_request';
              const text = (
                <>
                  <span className={styles.attentionIcon}>
                    <Icon />
                  </span>
                  <span className={styles.attentionText}>
                    <strong>{sentence(item)}</strong>
                    {item.dealName && <span>{attentionContext(item)}</span>}
                    {isSupport && (
                      <span>
                        {[
                          item.subject,
                          (item.count ?? 0) > 1 &&
                            t('crm.attention.support_emails', {
                              count: item.count ?? 0,
                            }),
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    )}
                    {item.summary && <em>{item.summary}</em>}
                  </span>
                </>
              );
              return (
                <li
                  key={item.key}
                  className={styles.attentionRow}
                  data-severity={item.severity}
                >
                  {isSupport ? (
                    <div className={styles.attentionMain}>{text}</div>
                  ) : (
                    <button
                      type="button"
                      className={styles.attentionMain}
                      onClick={() => open(item)}
                    >
                      {text}
                    </button>
                  )}
                  {owner && (
                    <span
                      className={styles.attentionOwner}
                      title={personName(owner)}
                    >
                      {personInitials(owner)}
                    </span>
                  )}
                  <div className={styles.attentionActions}>
                    {replyTo &&
                      (mailApp ? (
                        <CrmButton
                          variant="outline"
                          IconPrefix={Reply}
                          title={`${t('crm.attention.reply_in')} ${t(`crm.attention.mail_apps.${mailApp}`)}`}
                          onClick={() => void reply(item, mailApp)}
                        >
                          {t('crm.attention.reply')}
                        </CrmButton>
                      ) : (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <CrmButton variant="outline" IconPrefix={Reply}>
                              {t('crm.attention.reply')}
                            </CrmButton>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>
                              {t('crm.attention.reply_in')}
                            </DropdownMenuLabel>
                            {CRM_MAIL_APPS.map((app) => (
                              <DropdownMenuItem
                                key={app}
                                onSelect={() => void reply(item, app)}
                              >
                                {t(`crm.attention.mail_apps.${app}`)}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ))}
                    {isSupport && (
                      <CrmButton
                        variant="outline"
                        IconPrefix={Check}
                        disabled={busy === item.key}
                        onClick={() => void handle(item)}
                      >
                        {t('crm.attention.handled')}
                      </CrmButton>
                    )}
                    {isTask && (
                      <CrmButton
                        variant="outline"
                        IconPrefix={Check}
                        disabled={busy === item.key}
                        onClick={() => void complete(item)}
                      >
                        {t('crm.attention.done')}
                      </CrmButton>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <CrmButton
                          variant="ghost"
                          IconPrefix={Clock}
                          disabled={busy === item.key}
                        >
                          {t('crm.attention.later')}
                        </CrmButton>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={() => void snooze(item, 1)}>
                          {t('crm.attention.later_day')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => void snooze(item, 7)}>
                          {t('crm.attention.later_week')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </li>
              );
            })}
          </ul>
          {items.length > FIRST_ROWS && (
            <CrmButton
              variant="ghost"
              className={styles.attentionMore}
              onClick={() => setExpanded(!expanded)}
            >
              {expanded
                ? t('crm.attention.show_less')
                : t('crm.attention.show_all', { count: items.length })}
            </CrmButton>
          )}
        </>
      ) : (
        <div className={styles.quietState}>
          <CalendarCheck2 />
          <h3>{t('crm.workspace.no_tasks')}</h3>
          <p>
            {t(
              tab === 'support'
                ? 'crm.attention.support_empty'
                : 'crm.attention.empty'
            )}
          </p>
        </div>
      )}
    </section>
  );
};
